<template>
  <div class="modal fade upload-plot-modal" tabindex="-1" role="dialog" aria-labelledby="myLargeModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-uppercase" id="myLargeModalLabel">Upload Land Rate data</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="uploading"></button>
        </div>

        <div class="row" style="margin-left: 10px;margin-top: 10px">
          <a :href="uploadTemplate()">Download Template</a>
        </div>

        <div class="modal-body">
          <!-- Upload Progress Section -->
          <div v-if="uploading" class="upload-progress-section mb-4">
            <div class="alert alert-info">
              <h6 class="mb-3">
                <i class="bx bx-upload me-2"></i>
                Upload in Progress... Please do not close this window
              </h6>

              <!-- Progress Bar -->
              <div class="progress mb-3" style="height: 30px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated"
                     role="progressbar"
                     :style="{width: uploadProgress.percentage + '%'}"
                     :class="uploadProgress.percentage === 100 ? 'bg-success' : 'bg-primary'">
                  {{ uploadProgress.percentage }}%
                </div>
              </div>

              <!-- Upload Statistics -->
              <div class="row text-center mb-3">
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0 text-primary">{{ uploadProgress.currentBatch }}</h4>
                    <small class="text-muted">Current Batch</small>
                  </div>
                </div>
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0 text-success">{{ uploadProgress.totalUploaded }}</h4>
                    <small class="text-muted">Uploaded</small>
                  </div>
                </div>
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0 text-danger">{{ uploadProgress.totalFailed }}</h4>
                    <small class="text-muted">Failed</small>
                  </div>
                </div>
                <div class="col-3">
                  <div class="stat-box">
                    <h4 class="mb-0">{{ uploadProgress.totalRecords }}</h4>
                    <small class="text-muted">Total Records</small>
                  </div>
                </div>
              </div>

              <!-- Time Estimation -->
              <div class="row text-center">
                <div class="col-6">
                  <p class="mb-0">
                    <i class="bx bx-time me-1"></i>
                    <strong>Elapsed:</strong> {{ formatTime(uploadProgress.elapsedTime) }}
                  </p>
                </div>
                <div class="col-6">
                  <p class="mb-0">
                    <i class="bx bx-hourglass me-1"></i>
                    <strong>Remaining:</strong> {{ formatTime(uploadProgress.estimatedTimeRemaining) }}
                  </p>
                </div>
              </div>

              <!-- Speed Info -->
              <div class="text-center mt-2">
                <small class="text-muted">
                  Speed: {{ uploadProgress.recordsPerSecond }} records/sec
                </small>
              </div>

              <!-- Pause/Cancel Buttons -->
              <div class="text-center mt-3">
                <button @click="pauseUpload" class="btn btn-warning btn-sm me-2" v-if="!uploadPaused">
                  <i class="bx bx-pause"></i> Pause
                </button>
                <button @click="resumeUpload" class="btn btn-success btn-sm me-2" v-if="uploadPaused">
                  <i class="bx bx-play"></i> Resume
                </button>
                <button @click="cancelUpload" class="btn btn-danger btn-sm">
                  <i class="bx bx-x"></i> Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Upload Configuration Section -->
          <div class="row mb-4" v-if="!uploading">
            <div class="col-4">
              <div class="form-group mb-3">
                <label for="">Select Sub County</label>
                <select class="form-select font-13px form-control" @change="handleSubCountyChange" v-model="selectedSubCounty">
                  <option value="">Select Sub County</option>
                  <option v-for="(item, index) in subCounties" :key="`${index}`" :value="item">{{item.subCountyName}}</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="form-group mb-3">
                <label for="">Select Market/Zone</label>
                <select class="form-select font-13px form-control" v-model="selectedZone">
                  <option value="">Select Zone</option>
                  <option :key="`${index}`" :value="item.zone" v-for="(item, index) in zones">{{item.zone}}</option>
                </select>
              </div>
            </div>

            <div class="col-4">
              <div class="form-group mb-3">
                <label for="">Batch Size (records per request)</label>
                <select class="form-select font-13px form-control" v-model="batchSize">
                  <option :value="5">5 records</option>
                  <option :value="10">10 records</option>
                  <option :value="20">20 records</option>
                  <option :value="50">50 records</option>
                  <option :value="100">100 records</option>
                </select>
                <small class="text-muted">Smaller batches = slower but more stable</small>
              </div>
            </div>
          </div>

          <!-- File Upload Section -->
          <div class="row mb-4" v-if="!fileData && !uploading">
            <div class="col-12">
              <div class="form-group">
                <label for="" class="text-uppercase mb-3">Upload Excel Sheet</label>
              </div>
            </div>

            <div class="col-12">
              <div class="upload-area border border-2 border-dashed p-4 text-center"
                   @drop="handleDrop"
                   @dragover.prevent
                   @dragenter.prevent
                   style="border-color: #dee2e6; border-radius: 8px;">
                <div class="fallback">
                  <input @change="handleFileChange($event)"
                         name="file"
                         type="file"
                         accept=".xlsx,.xls,.csv"
                         ref="fileInput"
                         style="display: none;">
                </div>
                <div class="dz-message needsclick" @click="$refs.fileInput.click()">
                  <div class="mb-3">
                    <i class="display-4 text-muted bx bxs-cloud-upload"></i>
                  </div>
                  <h4>Drop files here or click to upload</h4>
                  <p class="text-muted">Supported formats: .xlsx, .xls, .csv</p>
                </div>
              </div>
            </div>
          </div>

          <!-- File Processing Loading -->
          <div class="row mb-4" v-if="processing">
            <div class="col-12 text-center">
              <div class="spinner-border text-primary me-2" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <span>Processing file...</span>
            </div>
          </div>

          <!-- File Preview Section -->
          <div class="row" v-if="fileData && !processing && !uploading">
            <div class="col-12 mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">File Preview: {{ fileName }}</h6>
                <div>
                  <span class="badge bg-info me-2">{{ fileData.length }} rows</span>
                  <button @click="clearFile" class="btn btn-sm btn-outline-danger">
                    <i class="bx bx-x"></i> Clear File
                  </button>
                </div>
              </div>
            </div>

            <!-- Data Validation Summary -->
            <div class="col-12 mb-3" v-if="validationSummary">
              <div class="alert" :class="validationSummary.hasErrors ? 'alert-warning' : 'alert-success'">
                <h6 class="alert-heading">Validation Summary</h6>
                <div class="row">
                  <div class="col-md-3">
                    <strong>Total Rows:</strong> {{ validationSummary.totalRows }}
                  </div>
                  <div class="col-md-3">
                    <strong>Valid Rows:</strong> {{ validationSummary.validRows }}
                  </div>
                  <div class="col-md-3">
                    <strong>Invalid Rows:</strong> {{ validationSummary.invalidRows }}
                  </div>
                  <div class="col-md-3">
                    <strong>Estimated Batches:</strong> {{ Math.ceil(validationSummary.validRows / batchSize) }}
                  </div>
                </div>
                <div class="mt-2">
                  <div v-if="validationSummary.hasErrors" class="mb-2">
                    <small class="text-warning">
                      <i class="bx bx-warning"></i>
                      Please review the data below. Rows with errors are highlighted.
                    </small>
                  </div>
                  <div class="text-info">
                    <small>
                      <i class="bx bx-info-circle"></i>
                      <strong>Note:</strong> Records will be uploaded in batches of {{ batchSize }}.
                      The ZONE/MARKET for all records will be set to "<strong>{{ selectedZone }}</strong>".
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Data Table Preview (first 20 rows only) -->
            <div class="col-12">
              <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-sm table-striped">
                  <thead class="table-dark sticky-top">
                  <tr>
                    <th style="width: 50px;">#</th>
                    <th v-for="(header, index) in headers.slice(0, 8)" :key="index">
                      {{ header }}
                      <span v-if="requiredColumns.includes(header)" class="text-danger">*</span>
                    </th>
                    <th style="width: 100px;">Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(row, index) in fileData.slice(0, 20)"
                      :key="index"
                      :class="row._hasErrors ? 'table-warning' : ''">
                    <td>{{ index + 1 }}</td>
                    <td v-for="(header, headerIndex) in headers.slice(0, 8)" :key="headerIndex">
                      <span v-if="row._errors && row._errors[header]"
                            class="text-danger"
                            :title="row._errors[header]">
                          {{ row[header] || '-' }}
                          <i class="bx bx-error-circle"></i>
                        </span>
                      <span v-else>{{ row[header] || '-' }}</span>
                    </td>
                    <td>
                        <span v-if="row._hasErrors" class="badge bg-warning">
                          <i class="bx bx-error"></i> Error
                        </span>
                      <span v-else class="badge bg-success">
                          <i class="bx bx-check"></i> Valid
                        </span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p class="text-muted text-center mt-2" v-if="fileData.length > 20">
                <small>Showing first 20 rows of {{ fileData.length }} total rows</small>
              </p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="uploading">
            <i class="bx bx-x font-size-16 align-middle me-2"></i> Cancel
          </button>
          <button @click="handleUpload"
                  type="button"
                  class="btn btn-primary waves-effect waves-light"
                  :disabled="!canUpload || uploading"
                  v-if="!uploading">
            <i class="bx bx-check font-size-16 align-middle me-2"></i>
            Start Batch Upload
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import * as XLSX from 'xlsx';
import {uploadTemplates} from "@/api";

export default {
  name: 'UploadPlotModal',
  props: {
    subCounties: {
      type: Array,
      default: () => []
    },
    zones: {
      type: Array,
      default: () => []
    }
  },
  emits: ['upload-batch', 'upload-complete', 'get-zones'],
  data() {
    return {
      selectedSubCounty: null,
      selectedZone: '',
      selectedFile: null,
      fileName: '',
      fileData: null,
      headers: [],
      processing: false,
      uploading: false,
      uploadPaused: false,
      uploadCancelled: false,
      currentPage: 1,
      pageSize: 20,
      validationSummary: null,
      requiredColumns: ['UPN', 'Owner Name'],
      batchSize: 10,

      // Upload progress tracking
      uploadProgress: {
        currentBatch: 0,
        totalBatches: 0,
        totalUploaded: 0,
        totalFailed: 0,
        totalRecords: 0,
        percentage: 0,
        startTime: null,
        elapsedTime: 0,
        estimatedTimeRemaining: 0,
        recordsPerSecond: 0
      },

      // Batch upload state
      batchUploadState: {
        batches: [],
        currentBatchIndex: 0,
        allFailedRecords: [],
        uploadMetadata: null
      },

      uploadTimer: null
    }
  },
  computed: {
    canUpload() {
      return this.selectedSubCounty &&
          this.selectedSubCounty.subCountyName &&
          this.selectedZone &&
          this.selectedZone.trim() !== '' &&
          this.fileData &&
          this.fileData.length > 0 &&
          this.validationSummary &&
          this.validationSummary.validRows > 0;
    }
  },
  mounted() {
    const modalElement = this.$el;
    modalElement.addEventListener('hidden.bs.modal', () => {
      if (!this.uploading) {
        this.clearFile();
        this.selectedSubCounty = null;
        this.selectedZone = '';
      }
    });
  },
  beforeUnmount() {
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
    }
  },
  methods: {
    uploadTemplate() {
      return uploadTemplates + 'LANDRATE-TEMPLATE.xls'
    },

    handleSubCountyChange() {
      this.selectedZone = '';
      if (this.selectedSubCounty) {
        this.$emit('get-zones', this.selectedSubCounty.subCountyID);
      }
    },

    handleDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    processFile(file) {
      this.selectedFile = file;
      this.fileName = file.name;
      this.processing = true;
      this.fileData = null;
      this.headers = [];

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let data;
          const arrayBuffer = e.target.result;

          if (file.name.endsWith('.csv')) {
            const text = new TextDecoder().decode(arrayBuffer);
            data = this.parseCSV(text);
          } else {
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          }

          this.processData(data);
        } catch (error) {
          console.error('Error processing file:', error);
          alert('Error processing file. Please check the file format and try again.');
        } finally {
          this.processing = false;
        }
      };

      reader.readAsArrayBuffer(file);
    },

    parseCSV(text) {
      const lines = text.split('\n');
      const result = [];

      lines.forEach(line => {
        if (line.trim()) {
          const values = line.split(',').map(value => value.trim().replace(/^"|"$/g, ''));
          result.push(values);
        }
      });

      return result;
    },

    processData(rawData) {
      if (!rawData || rawData.length === 0) {
        alert('No data found in the file.');
        return;
      }

      this.headers = rawData[0];
      const dataRows = rawData.slice(1);

      this.fileData = dataRows.map((row, index) => {
        const rowObject = {};
        this.headers.forEach((header, headerIndex) => {
          rowObject[header] = row[headerIndex] || '';
        });

        const validation = this.validateRow(rowObject, index + 2);
        rowObject._hasErrors = validation.hasErrors;
        rowObject._errors = validation.errors;

        return rowObject;
      });

      this.generateValidationSummary();
      this.currentPage = 1;
    },

    validateRow(row, rowNumber) {
      const errors = {};
      let hasErrors = false;

      this.requiredColumns.forEach(column => {
        if (!row[column] || row[column].toString().trim() === '') {
          errors[column] = `${column} is required`;
          hasErrors = true;
        }
      });

      if (row.PHONE && row.PHONE.toString().trim() !== '' && !/^[0-9+\-\s()]{6,}$/.test(row.PHONE.toString().trim())) {
        errors.PHONE = 'Invalid phone number format';
        hasErrors = true;
      }

      if (row.EMAIL && row.EMAIL.toString().trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.EMAIL.toString().trim())) {
        errors.EMAIL = 'Invalid email format';
        hasErrors = true;
      }

      return { hasErrors, errors };
    },

    generateValidationSummary() {
      const totalRows = this.fileData.length;
      const invalidRows = this.fileData.filter(row => row._hasErrors).length;
      const validRows = totalRows - invalidRows;
      const missingRequired = this.fileData.filter(row => {
        return this.requiredColumns.some(col => !row[col] || row[col].toString().trim() === '');
      }).length;

      this.validationSummary = {
        totalRows,
        validRows,
        invalidRows,
        missingRequired,
        hasErrors: invalidRows > 0
      };
    },

    clearFile() {
      this.selectedFile = null;
      this.fileName = '';
      this.fileData = null;
      this.headers = [];
      this.validationSummary = null;
      this.currentPage = 1;
      this.resetUploadProgress();

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    resetUploadProgress() {
      this.uploadProgress = {
        currentBatch: 0,
        totalBatches: 0,
        totalUploaded: 0,
        totalFailed: 0,
        totalRecords: 0,
        percentage: 0,
        startTime: null,
        elapsedTime: 0,
        estimatedTimeRemaining: 0,
        recordsPerSecond: 0
      };

      this.batchUploadState = {
        batches: [],
        currentBatchIndex: 0,
        allFailedRecords: [],
        uploadMetadata: null
      };

      if (this.uploadTimer) {
        clearInterval(this.uploadTimer);
        this.uploadTimer = null;
      }
    },

    handleUpload() {
      if (!this.selectedSubCounty || !this.selectedSubCounty.subCountyName) {
        alert('Please select a Sub County');
        return;
      }

      if (!this.selectedZone || this.selectedZone.trim() === '') {
        alert('Please select a Market/Zone');
        return;
      }

      if (!this.fileData || this.fileData.length === 0) {
        alert('Please upload and process a valid file first');
        return;
      }

      if (!this.validationSummary || this.validationSummary.validRows === 0) {
        alert('No valid rows found in the uploaded file. Please check the data and try again.');
        return;
      }

      const confirmMessage = `
        Ready to start batch upload:
        - Total valid records: ${this.validationSummary.validRows}
        - Batch size: ${this.batchSize} records per request
        - Estimated batches: ${Math.ceil(this.validationSummary.validRows / this.batchSize)}
        - Zone: ${this.selectedZone}
        - Sub County: ${this.selectedSubCounty.subCountyName}

        This will take approximately ${this.estimateUploadTime()} minutes.

        Do you want to proceed?
      `;

      if (!confirm(confirmMessage)) {
        return;
      }

      this.startBatchUpload();
    },

    estimateUploadTime() {
      const validRows = this.validationSummary.validRows;
      const batches = Math.ceil(validRows / this.batchSize);
      const estimatedSeconds = batches * 2; // Assume 2 seconds per batch
      return Math.ceil(estimatedSeconds / 60);
    },

    startBatchUpload() {
      this.uploading = true;
      this.uploadPaused = false;
      this.uploadCancelled = false;

      // Filter valid data
      const validData = this.fileData
          .filter(row => !row._hasErrors)
          .map(row => {
            const cleanRow = { ...row };
            delete cleanRow._hasErrors;
            delete cleanRow._errors;

            return {
              upn: cleanRow.UPN || '',
              LRNumber: cleanRow.UPN || '',
              blockNumber: cleanRow['Block Number'] || '',
              plotNo: cleanRow['Plot No.'] || '',
              ownerNames: cleanRow['Owner Name'] || '',
              idNo: cleanRow['ID NO'] || cleanRow['Idno'] || cleanRow['idNo'] || cleanRow['ID Number'] || '',
              landRatesArrears: parseFloat(cleanRow['Land Rates Arrears']) || 0,
              groundRentArrears: parseFloat(cleanRow['Ground Rent Arrears']) || 0,
              accumPenalty: parseFloat(cleanRow['Accum. Penalty(Rent)']) || 0,
              accumInterest: parseFloat(cleanRow['Accum. Interest(Lates)']) || 0,
              annualLandRates: parseFloat(cleanRow['Annual Land Rates']) || 0,
              annualGroundRent: parseFloat(cleanRow['Annual Ground Rent']) || 0,
              payments: parseFloat(cleanRow['Payments']) || 0,
              currentBalance: parseFloat(cleanRow['Current Balance']) || 0,
              percelSize: parseFloat(cleanRow['PARCEL SIZE']) || 0,
              percelAddress: cleanRow['PHYSICAL ADDRESS'] || '',
              ownerEmail: cleanRow['EMAIL'] || '',
              ownerPhoneNo: cleanRow['PHONE'] || '',
              zone: this.selectedZone,
              block: cleanRow['BLOCK'] || '',
              useName: cleanRow['USE[R]'] || '',
              lat: parseFloat(cleanRow['LATITUDE']) || null,
              lng: parseFloat(cleanRow['LONGITUDE']) || null,
              outstandingArrears: (parseFloat(cleanRow['Land Rates Arrears']) || 0) + (parseFloat(cleanRow['Ground Rent Arrears']) || 0),
              annualCharges: (parseFloat(cleanRow['Annual Land Rates']) || 0) + (parseFloat(cleanRow['Annual Ground Rent']) || 0)
            };
          });

      // Split into batches
      const batches = [];
      for (let i = 0; i < validData.length; i += this.batchSize) {
        batches.push(validData.slice(i, i + this.batchSize));
      }

      // Initialize state
      this.batchUploadState.batches = batches;
      this.batchUploadState.currentBatchIndex = 0;
      this.batchUploadState.allFailedRecords = [];
      this.batchUploadState.uploadMetadata = {
        zone: this.selectedZone,
        subCountyID: this.selectedSubCounty.subCountyID,
        subCountyName: this.selectedSubCounty.subCountyName,
        fileName: this.fileName,
        uploadedBy: sessionStorage.getItem("sessionNames"),
        uploadedByID: sessionStorage.getItem("sessionIdNo"),
        uploadDate: new Date().toISOString()
      };

      this.uploadProgress.totalBatches = batches.length;
      this.uploadProgress.totalRecords = validData.length;
      this.uploadProgress.startTime = Date.now();

      // Start timer for elapsed time
      this.uploadTimer = setInterval(() => {
        if (!this.uploadPaused) {
          this.uploadProgress.elapsedTime = Math.floor((Date.now() - this.uploadProgress.startTime) / 1000);
          this.updateTimeEstimation();
        }
      }, 1000);

      // Start uploading
      this.uploadNextBatch();
    },

    async uploadNextBatch() {
      if (this.uploadCancelled) {
        this.finishUpload();
        return;
      }

      if (this.uploadPaused) {
        return;
      }

      if (this.batchUploadState.currentBatchIndex >= this.batchUploadState.batches.length) {
        this.finishUpload();
        return;
      }

      const currentBatch = this.batchUploadState.batches[this.batchUploadState.currentBatchIndex];
      this.uploadProgress.currentBatch = this.batchUploadState.currentBatchIndex + 1;

      // Prepare batch payload
      const batchPayload = {
        plotData: currentBatch,
        uploadMetadata: {
          ...this.batchUploadState.uploadMetadata,
          batchNumber: this.batchUploadState.currentBatchIndex + 1,
          totalBatches: this.batchUploadState.batches.length,
          batchSize: currentBatch.length
        }
      };

      // Emit to parent for API call
      this.$emit('upload-batch', batchPayload, this.handleBatchResponse);
    },

    handleBatchResponse(response) {
      if (response.success) {
        this.uploadProgress.totalUploaded += response.data.successful || 0;
        this.uploadProgress.totalFailed += response.data.failed || 0;

        if (response.data.failedRecords && response.data.failedRecords.length > 0) {
          this.batchUploadState.allFailedRecords.push(...response.data.failedRecords);
        }
      } else {
        // If batch completely failed, count all records as failed
        const batchSize = this.batchUploadState.batches[this.batchUploadState.currentBatchIndex].length;
        this.uploadProgress.totalFailed += batchSize;
      }

      // Update progress
      this.batchUploadState.currentBatchIndex++;
      this.uploadProgress.percentage = Math.round((this.batchUploadState.currentBatchIndex / this.uploadProgress.totalBatches) * 100);

      // Calculate speed
      const elapsedSeconds = this.uploadProgress.elapsedTime || 1;
      this.uploadProgress.recordsPerSecond = Math.round((this.uploadProgress.totalUploaded + this.uploadProgress.totalFailed) / elapsedSeconds);

      // Continue to next batch
      if (this.batchUploadState.currentBatchIndex < this.batchUploadState.batches.length) {
        setTimeout(() => this.uploadNextBatch(), 500); // Small delay between batches
      } else {
        this.finishUpload();
      }
    },

    updateTimeEstimation() {
      const processedBatches = this.batchUploadState.currentBatchIndex;
      const remainingBatches = this.uploadProgress.totalBatches - processedBatches;

      if (processedBatches > 0) {
        const avgTimePerBatch = this.uploadProgress.elapsedTime / processedBatches;
        this.uploadProgress.estimatedTimeRemaining = Math.round(avgTimePerBatch * remainingBatches);
      }
    },

    pauseUpload() {
      this.uploadPaused = true;
    },

    resumeUpload() {
      this.uploadPaused = false;
      this.uploadNextBatch();
    },

    cancelUpload() {
      if (confirm('Are you sure you want to cancel the upload? Already uploaded records will remain in the database.')) {
        this.uploadCancelled = true;
        this.finishUpload();
      }
    },

    finishUpload() {
      this.uploading = false;

      if (this.uploadTimer) {
        clearInterval(this.uploadTimer);
        this.uploadTimer = null;
      }

      const finalResults = {
        successful: this.uploadProgress.totalUploaded,
        failed: this.uploadProgress.totalFailed,
        totalProcessed: this.uploadProgress.totalUploaded + this.uploadProgress.totalFailed,
        failedRecords: this.batchUploadState.allFailedRecords,
        cancelled: this.uploadCancelled,
        message: this.uploadCancelled
            ? `Upload cancelled. ${this.uploadProgress.totalUploaded} records uploaded before cancellation.`
            : `Batch upload completed. ${this.uploadProgress.totalUploaded} records uploaded successfully.`
      };

      this.$emit('upload-complete', finalResults);

      // Don't clear file immediately, let user see results
      setTimeout(() => {
        if (!this.uploading) {
          this.clearFile();
        }
      }, 2000);
    },

    formatTime(seconds) {
      if (!seconds || seconds < 0) return '00:00';

      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  }
}
</script>

<style scoped>
.upload-area {
  cursor: pointer;
  transition: border-color 0.3s ease;
}

.upload-area:hover {
  border-color: #007bff !important;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

.table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.badge {
  font-size: 0.75em;
}

.text-danger i {
  margin-left: 0.25rem;
}

.upload-progress-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.stat-box {
  padding: 0.5rem;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.progress {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>



